import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';

import { DividerModule } from 'primeng/divider';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { AvatarModule } from 'primeng/avatar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TagModule } from 'primeng/tag';
import { TimelineModule } from 'primeng/timeline';
import { TooltipModule } from 'primeng/tooltip';

// Enums
import { HistoryEventKindEnum } from '../../enums/history-event-kind.enum';

// Modèles
import { HistoryEvent } from '../../models/core/history-event.model';
import { ModelBase } from '../../models/abstract/model-base.model';

// Services
import { ServiceBase } from 'src/app/@core/services/service-base';

/** Nombre d'événements chargés à chaque interrogation de l'API */
const PAGE_SIZE = 20;

/** Présentation d'un événement selon sa nature (couleur = nom de palette PrimeFlex) */
const KIND_PRESENTATIONS: Record<string, { title: string; icon: string; color: string }> = {
    [HistoryEventKindEnum.Creation]: { title: 'Création', icon: 'fa-solid fa-plus', color: 'blue' },
    [HistoryEventKindEnum.Update]: { title: 'Modification', icon: 'fa-solid fa-pen', color: 'yellow' },
    [HistoryEventKindEnum.Deletion]: { title: 'Suppression', icon: 'fa-solid fa-trash-can', color: 'red' },
    [HistoryEventKindEnum.Restoration]: { title: 'Restauration', icon: 'fa-solid fa-rotate-left', color: 'blue' },
    [HistoryEventKindEnum.StatusSet]: { title: 'Statut appliqué', icon: 'fa-solid fa-flag', color: 'green' },
    [HistoryEventKindEnum.StatusRemoved]: { title: 'Statut retiré', icon: 'fa-regular fa-flag', color: 'gray' },
};

/** Couleur des événements d'origine automatique, prioritaire sur celle du kind */
const SYSTEM_COLOR = 'purple';
/** Couleur de repli pour un kind inconnu */
const DEFAULT_COLOR = 'gray';

/** Élément de la timeline : séparateur de date ou événement pré-calculé pour affichage */
interface TimelineItem {
    borderClass?: string;
    date?: string;
    dotClass?: string;
    event?: HistoryEvent;
    icon?: string;
    iconBgClass?: string;
    iconClass?: string;
    subtitle?: string | null;
    subtitleClass?: string;
    system?: boolean;
    time?: string;
    timeExact?: string;
    title?: string;
    titleClass?: string;
}

type HistoryFilter = 'all' | 'system' | 'human';

/**
 * Timeline de l'historique d'un BusinessObject (dialog `OBJECT_HISTORY_DIALOG`).
 *
 * Affiche les événements renvoyés par l'endpoint générique `{id}/history/` :
 * actions automatiques (utilisateurs de service) à gauche, actions humaines à
 * droite. Chargement paginé par PAGE_SIZE au fil du scroll.
 *
 * Données attendues dans la config du dialog :
 * - `source` : l'objet dont on affiche l'historique
 * - `service` : le service (ServiceBase) de cet objet
 */
@Component({
    selector: 'app-object-history',
    host: { style: 'display: flex; flex-direction: column; height: 100%; min-height: 0' },
    imports: [
        CommonModule,
        FormsModule,
        AvatarModule,
        DividerModule,
        ProgressSpinnerModule,
        SelectButtonModule,
        TagModule,
        TimelineModule,
        TooltipModule,
    ],
    templateUrl: './object-history.component.html',
    styleUrl: './object-history.component.scss'
})
export class ObjectHistoryComponent implements OnInit {

    private _config = inject(DynamicDialogConfig);

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/

    @ViewChild('scrollContainer')
    private _scrollContainer!: ElementRef<HTMLDivElement>;

    public expandedEvent: HistoryEvent | null = null;
    public loading: boolean = false;

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    public events: HistoryEvent[] = [];
    public filter: HistoryFilter = 'all';
    public filterOptions = [
        { label: 'Tout', value: 'all' },
        { label: 'Système', value: 'system' },
        { label: 'Humain', value: 'human' },
    ];
    public hasMore: boolean = true;
    public timelineItems: TimelineItem[] = [];
    public visibleCount: number = 0;

    private _service!: ServiceBase;
    private _source!: ModelBase;

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    ngOnInit(): void {
        this._source = this._config.data.source;
        this._service = this._config.data.service;
        this._loadNextPage();
    }

    public onChangeFilter(): void {
        this._applyFilter();
    }

    public onClickEvent(item: TimelineItem): void {
        this._toggleEvent(item.event!);
    }

    public onScrollTimeline(): void {
        this._loadOnScroll();
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /**
     * Reconstruit la liste des filtres appliqués et les éléments de timeline
     */
    private _applyFilter(): void {
        this.expandedEvent = null;
        this._buildTimelineItems();
    }

    /**
     * Construit les éléments de la timeline à partir des événements chargés :
     * application du filtre système/humain puis insertion des séparateurs de date.
     * Pré-calcule les données d'affichage pour éviter tout getter coûteux dans le template.
     */
    private _buildTimelineItems(): void {
        const filtered = this.events.filter(event => {
            if (this.filter === 'system') {
                return event.isSystem;
            }
            if (this.filter === 'human') {
                return !event.isSystem;
            }
            return true;
        });

        const items: TimelineItem[] = [];
        let currentDate: string | null = null;
        for (const event of filtered) {
            const day = event.date.format('DD/MM/YYYY');
            if (day !== currentDate) {
                currentDate = day;
                items.push({ date: day });
            }
            const color = this._eventColor(event);
            items.push({
                borderClass: `border-${color}-400`,
                dotClass: `bg-${color}-400`,
                event: event,
                icon: this._eventIcon(event),
                iconBgClass: `bg-${color}-100`,
                iconClass: `text-${color}-900`,
                subtitle: this._eventSubtitle(event),
                subtitleClass: event.isStatusEvent ? 'font-italic text-gray-400' : 'text-color-secondary',
                system: event.isSystem,
                time: event.date.format('HH:mm'),
                timeExact: event.date.format('DD/MM/YYYY HH:mm:ss'),
                title: this._eventTitle(event),
                titleClass: `text-${color}-500`,
            });
        }

        this.timelineItems = items;
        this.visibleCount = filtered.length;
    }

    /**
     * Recharge une page si le contenu ne remplit pas la zone de scroll
     * (sinon l'utilisateur ne pourrait jamais déclencher la suite)
     */
    private _checkContentFillsViewport(): void {
        setTimeout(() => {
            const container = this._scrollContainer?.nativeElement;
            if (!!container && container.scrollHeight <= container.clientHeight && this.hasMore && !this.loading) {
                this._loadNextPage();
            }
        }, 100);
    }

    /**
     * Couleur d'un événement : violet pour toute action automatique,
     * sinon la couleur associée à sa nature
     */
    private _eventColor(event: HistoryEvent): string {
        if (event.isSystem) {
            return SYSTEM_COLOR;
        }
        return KIND_PRESENTATIONS[event.kind]?.color || DEFAULT_COLOR;
    }

    private _eventIcon(event: HistoryEvent): string {
        return KIND_PRESENTATIONS[event.kind]?.icon || 'fa-solid fa-circle-info';
    }

    private _eventSubtitle(event: HistoryEvent): string | null {
        if (event.isStatusEvent) {
            return KIND_PRESENTATIONS[event.kind]?.title || null;
        }
        return event.scope?.label || null;
    }

    private _eventTitle(event: HistoryEvent): string {
        if (event.isStatusEvent && !!event.status) {
            return event.status.label;
        }
        return KIND_PRESENTATIONS[event.kind]?.title || event.kind;
    }

    /**
     * Charge la page suivante d'événements ($top/$skip)
     */
    private _loadNextPage(): void {
        if (this.loading || !this.hasMore || !this._source?.id) {
            return;
        }
        this.loading = true;
        this._service.getHistory$(this._source.id, { top: PAGE_SIZE, skip: this.events.length }).pipe(
            finalize(() => {
                this.loading = false;
                this._checkContentFillsViewport();
            })
        ).subscribe((batch: HistoryEvent[]) => {
            this.events = this.events.concat(batch);
            this.hasMore = batch.length === PAGE_SIZE;
            this._buildTimelineItems();
        });
    }

    /**
     * Déclenche le chargement de la page suivante à l'approche du bas de la zone de scroll
     */
    private _loadOnScroll(): void {
        const container = this._scrollContainer?.nativeElement;
        if (!container) {
            return;
        }
        const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        if (distanceToBottom < 200) {
            this._loadNextPage();
        }
    }

    private _toggleEvent(event: HistoryEvent): void {
        if (!event.hasDetails) {
            return;
        }
        this.expandedEvent = this.expandedEvent === event ? null : event;
    }
}
